/* eslint-disable no-undef */
import React, { Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import qs from 'query-string';

import {
  AccountManager,
  ApiManager,
  BetManager,
  Bus,
  isNotEmptyObject,
  SettingsManager,
  SiteConfigManager,
  SportManager,
  SubscriptionManager,
  initDebug,
} from '@atlas/core';
import { COMPONENT_TYPES, staticRoutes } from '@atlas/routing';
import { Messages, TransportManager } from '@atlas/transport';

import Listen from 'common/Affiltate/init';
import network from 'common/Affiltate/Manager';
import { CDN_TYPE_SPORT, getCDNUrl, LocalConfig } from 'common/utils';
import history from 'components/Layout/history';
import subscribeUserOffline from 'src/subscriptions/subscribeUserOffline';

import 'common/Form/formConfig';
import '../subscriptions/dataLayer.bindings';
import '../subscriptions/iframe.bindings';

import packageData from '../../package.json';
import IFrameRootLayout from '../iframe';
import busEventsMapping from '../subscriptions/bus.bindings';
import { dataDogInit } from 'helpers/dataDog';

dataDogInit();
initDebug();

const ShareCss = React.lazy(() => import('modules/share-css'));

const SuspenseCssComponent = () => (
  <Suspense fallback={<div />}>
    <ShareCss />
  </Suspense>
);

SiteConfigManager.setLayoutConfig({
  [COMPONENT_TYPES.SPORT_LIVE]: {
    defaultPath: staticRoutes.live,
  },
  [COMPONENT_TYPES.SPORT_PREMATCH]: {
    defaultPath: staticRoutes.sportsbook,
  },
  [COMPONENT_TYPES.RESULTS]: {
    defaultPath: staticRoutes.results,
  },
});

// Only for development
if (process.env.NODE_ENV === 'development') {
  // eslint-disable-next-line import/extensions, global-require
  const localCmsSettings = require('./cms.settings.js');
  SiteConfigManager.setConfig(localCmsSettings.default);

  // Loading svg sprites from dyeing
  fetch('/dyeing/build/sprite/sprite.svg')
    .then(res => res.text())
    .then(svg => {
      document.body.insertAdjacentHTML('beforeend', svg);
    });
}

// Only for production
if (process.env.NODE_ENV === 'production') {
  // Applying default client application settings
  if (isNotEmptyObject(window.CMS_CONFIG)) {
    const { navigation = {}, page = {} } = window.CMS_CONFIG;

    // FIXME
    SiteConfigManager.setConfig({ ...page, navigation });
  }
}

const betslipCMSConfig = SiteConfigManager.getConfig('config.config.betslip');
if (betslipCMSConfig) {
  BetManager.updateBetslipSettings(betslipCMSConfig);
}

const defaultAcceptOddChanges = SiteConfigManager.getConfig(
  'config.config.betslip.defaultAcceptOddsChangesType',
);
if (defaultAcceptOddChanges) {
  SettingsManager.settings.acceptOddsChanges = defaultAcceptOddChanges;
}

const defaultOddsZeroes = SiteConfigManager.getConfig('config.config.odds_zeroes');
if (defaultOddsZeroes) {
  SettingsManager.settings.oddsZeroes = defaultOddsZeroes;
}

const defaultOddsFractionDigits = SiteConfigManager.getConfig('config.config.odds_fraction_digits');
if (defaultOddsFractionDigits) {
  SettingsManager.settings.oddsFractionDigits = defaultOddsFractionDigits;
}

const cnf = SiteConfigManager.getConfig('config.config.registry');

if (cnf) {
  network.setConfig(cnf);
  Listen();
}

busEventsMapping(Bus, history);
subscribeUserOffline();
// Setting application version & device type

// Initialy resolving location query params
const providedQueryParams = qs.parse(window.location.search);
const defaultHeaders = {
  device: 'desktop',
  version: packageData.version,
};

// Adding default lang settings if provided
if (providedQueryParams.lang) {
  SettingsManager.updateSettings({
    lang: providedQueryParams.lang,
  });
}

// Adding currency code if provided
if (providedQueryParams.currency) {
  AccountManager.manuallySetCurrency = providedQueryParams.currency;
}

if (providedQueryParams.theme) {
  SettingsManager.updateSettings({
    theme: providedQueryParams.theme,
  });
}

if (providedQueryParams.footerUrl) {
  SettingsManager.updateSettings({
    footerUrl: providedQueryParams.footerUrl,
  });
}

if (providedQueryParams.footerHeight) {
  SettingsManager.updateSettings({
    footerHeight: providedQueryParams.footerHeight,
  });
}

const projectId = LocalConfig.get('projectId');

// Providing project id if it exist in config
if (projectId) {
  defaultHeaders['x-project-id'] = projectId;
}

// Providing header for local testing
if (process.env.NODE_ENV === 'development') {
  defaultHeaders['x-project-team'] = 'frontend-team';
}

ApiManager.setDefaultHeaders(defaultHeaders);

// If token provided
if (providedQueryParams.token) {
  ApiManager.setAuthorizationToken(providedQueryParams.token);
  AccountManager.setToken(providedQueryParams.token);
}

/*
  Overriding authentication subscription (need different notification topic)
*/
SubscriptionManager.authorizationSubscription = Messages.authorizedPartnerClientUpdates();

// Disables the ability to open links in a new window
const stopAll = e => {
  e.preventDefault();
  e.stopPropagation();
  return false;
};

if (document.addEventListener) {
  document.addEventListener(
    'click',
    e => {
      if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) {
        return stopAll(e);
      }
    },
    false,
  );
  document.addEventListener('contextmenu', e => stopAll(e), false);
} else {
  document.attachEvent('onclick', e => {
    if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) {
      return stopAll(e);
    }
  });
  document.attachEvent('oncontextmenu', e => stopAll(e));
}

// Resolving stored settings
SettingsManager.resolveSettings();

const { host, protocol } = window.location;
const webSocketProtocol = protocol.startsWith('https') ? 'wss:' : 'ws:';

ApiManager.setHostname('core', `${protocol}//${host}/api/`);
ApiManager.setHostname('cms', `${protocol}//${host}/api/cms-go/v1/site/`);
ApiManager.setHostname('cms/v2', `${protocol}//${host}/api/cms-go/v2/site/`);
ApiManager.setHostname('ws', `${webSocketProtocol}//${host}/ws/notifier2`);
ApiManager.setHostname('oddshub', `${webSocketProtocol}//${host}/odds-hub/ws`);
ApiManager.setHostname('media', 'http://app4.front-server.atlas-iac.com');
ApiManager.setHostname('cdn', getCDNUrl(CDN_TYPE_SPORT));

if (process.env.NODE_ENV === 'development') {
  window.BetManager = BetManager;
  window.Bus = Bus;
  window.AccountManager = AccountManager;
  window.SportManager = SportManager;
  window.SiteConfigManager = SiteConfigManager;
  window.TransportManager = TransportManager.getInstance();
}

const wsUrl = new URL(ApiManager.getHostname('ws'));
wsUrl.searchParams.set('projectId', projectId);
if (process.env.NODE_ENV === 'development') {
  // Added for security reason, special WAF rule created on Cloudflare
  wsUrl.searchParams.set('project-team-frontend-team', 'x');
}
const transport = TransportManager.getInstance();
transport.initialize(wsUrl);
transport.setOptions({ projectId });

const domNode = document.getElementById('root');
const root = createRoot(domNode);

root.render(
  <>
    <SuspenseCssComponent />
    <IFrameRootLayout />
  </>,
);
